import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { notify } from "../../utilities/toast";
import { requestPasswordReset } from "../../services/authentication-services";
import Loader from "../../components/Loader/Loader";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaEnvelope } from 'react-icons/fa';

interface ForgotPasswordForm {
    email: string;
}

const ForgotPassword = () => {
    const [loading, setLoading] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [sentEmail, setSentEmail] = useState("");
    const { t } = useTranslation();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ForgotPasswordForm>();

    const onSubmit = async (data: ForgotPasswordForm) => {
        try {
            const response = await requestPasswordReset(data.email, setLoading);
            
            if (response.status === 200) {
                setSentEmail(data.email);
                setIsEmailSent(true);
            } else if (response.error === "Password reset not available for social login accounts") {
                notify(t('forgotPassword.socialLoginError'), 'error');
            } else {
                notify(t('forgotPassword.serverError'), 'error');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            notify(t('forgotPassword.errorOccurred'), 'error');
        }
    };

    if (isEmailSent) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-100 dark:bg-black">
                <div className="max-w-md w-full space-y-8 bg-white dark:bg-slate-900 p-8 rounded-lg shadow-lg text-center">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900">
                            <FaCheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {t('forgotPassword.checkEmail')}
                        </h2>
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900">
                            <FaEnvelope className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-zinc-400">
                            {t('forgotPassword.emailSentTo')} <br />
                            <span className="font-medium text-gray-900 dark:text-white">{sentEmail}</span>
                        </p>
                        <p className="text-sm text-gray-500 dark:text-zinc-500 mt-2">
                            {t('forgotPassword.checkSpam')}
                        </p>
                    </div>
                    <div className="mt-6">
                        <Link
                            to="/login"
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {t('forgotPassword.backToLogin')}
                        </Link>
                    </div>
                    <div className="mt-4">
                        <button
                            onClick={() => setIsEmailSent(false)}
                            className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
                        >
                            {t('forgotPassword.tryAnotherEmail')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-100 dark:bg-black">
            <div className="max-w-md w-full space-y-8 bg-white dark:bg-slate-900 p-8 rounded-lg shadow-lg">
                <div>
                    <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-white">
                        {t('forgotPassword.title')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600 dark:text-zinc-400">
                        {t('forgotPassword.description')}
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-zinc-400">
                            {t('forgotPassword.emailLabel')}
                        </label>
                        <div className="mt-1">
                            <input
                                {...register("email", {
                                    required: t('forgotPassword.emailRequired'),
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: t('forgotPassword.invalidEmail')
                                    }
                                })}
                                type="email"
                                placeholder={t('forgotPassword.emailPlaceholder')}
                                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                        </div>
                        {errors.email && (
                            <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                        )}
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            {loading ? <Loader /> : t('forgotPassword.resetButton')}
                        </button>
                    </div>

                    <div className="text-center">
                        <p className="text-sm text-gray-600 dark:text-zinc-400">
                            {t('forgotPassword.rememberPassword')}{' '}
                            <Link to="/login" className="text-blue-600 hover:text-blue-500">
                                {t('forgotPassword.loginLink')}
                            </Link>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword; 